/**
 * @fileoverview Provide focused lead-profile, status, and workflow-context panels within `IntakeLeadDetail.js`. Panels own local edit interactions and explicit API submissions; the enclosing workspace owns accepted lead state and cross-panel refresh. Displayed workflow labels remain projections of backend records rather than authority to transition a lead.
 */

import React, { useContext, useEffect, useState } from 'react';
import ToastContext from '../../ToastContext';
import { updateIntakeLead } from './intakeApi';
import { formatDate, formatDateTimeLocal, STAGE_OPTIONS, STATUS_OPTIONS, titleize } from './intakeFormatters';
import AppIcon from "../shared/AppIcon";

const PROSPECT_FIELDS = [
{ key: 'prospect_name', label: 'Prospect' },
{ key: 'prospect_email', label: 'Email' },
{ key: 'prospect_phone', label: 'Phone' },
{ key: 'defendant_name_raw', label: 'Defendant' },
{ key: 'state_client', label: 'Client State' },
{ key: 'state_defendant', label: 'Defendant State' },
{ key: 'state_events', label: 'Event States' }];


const INTAKE_FIELDS = [
{ key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS, format: titleize },
{ key: 'current_stage', label: 'Stage', type: 'select', options: STAGE_OPTIONS, format: titleize },
{ key: 'category', label: 'Category', format: titleize },
{ key: 'assigned_owner_email', label: 'Owner' },
{ key: 'next_action_label', label: 'Next Action' },
{ key: 'next_action_at', label: 'Next Action At', type: 'datetime-local', format: formatDate },
{ key: 'staff_review_required', label: 'Staff Review', type: 'checkbox', format: (value) => value ? 'Required' : 'Not required' },
{ key: 'follow_ups_disabled', label: 'Follow-Ups', editLabel: 'Disable follow-ups', type: 'checkbox', format: (value) => value ? 'Disabled' : 'Enabled' },
{ key: 'ai_processing_locked', label: 'AI Lock', type: 'checkbox', format: (value) => value ? 'Locked' : 'Unlocked' },
{ key: 'potential_class_action', label: 'Potential Class Action', type: 'checkbox', format: (value) => value ? 'Tagged' : 'Not tagged' },
{ key: 'legal_hold', label: 'Legal Hold', type: 'checkbox', format: (value) => value ? 'Yes' : 'No' },
{ key: 'ai_processing_lock_reason', label: 'AI Lock Reason', type: 'textarea', wide: true },
{ key: 'potential_class_action_reason', label: 'Class Action Reason', type: 'textarea', wide: true },
{ key: 'staff_review_reason', label: 'Review Reason', type: 'textarea', wide: true }];




function getFormValue(lead, field) {
  if (field.type === 'datetime-local') return formatDateTimeLocal(lead[field.key]);
  if (field.type === 'checkbox') return Boolean(lead[field.key]);
  return lead[field.key] || '';
}



function buildForm(lead, fields) {
  return fields.reduce((form, field) => {
    form[field.key] = getFormValue(lead, field);
    return form;
  }, {});
}



function buildPayload(form, fields) {
  return fields.reduce((payload, field) => {
    payload[field.key] = field.type === 'datetime-local' ? form[field.key] || null : form[field.key];
    return payload;
  }, {});
}



function getFieldValue(lead, field) {
  const value = field.format ? field.format(lead[field.key]) : lead[field.key];
  return value;
}



function hasDisplayValue(lead, field) {
  if (field.type === 'checkbox') return true;
  const value = lead[field.key];
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(value);
}



function getDisplayValue(lead, field) {
  const value = getFieldValue(lead, field);
  return value && value !== '-' ? value : '';
}



function renderEditField(field, value, onChange, autoFocus) {
  if (field.type === 'select') {
    return (
      <select
        className="intake-inline-control"
        aria-label={field.label}
        autoFocus={autoFocus}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}>

                {field.options.map(([optionValue, optionLabel]) =>
        <option key={optionValue} value={optionValue}>{optionLabel}</option>
        )}
            </select>);

  }
  if (field.type === 'checkbox') {
    return (
      <label className="intake-inline-toggle">
                <input
          aria-label={field.editLabel || field.label}
          autoFocus={autoFocus}
          type="checkbox"
          checked={Boolean(value)}
          onChange={(event) => onChange(event.target.checked)} />

                <span>{value ? 'Yes' : 'No'}</span>
            </label>);

  }
  if (field.type === 'textarea') {
    return (
      <textarea
        className="intake-inline-control intake-inline-control--textarea"
        aria-label={field.label}
        autoFocus={autoFocus}
        value={value || ''}
        rows={2}
        onChange={(event) => onChange(event.target.value)} />);


  }
  return (
    <input
      className="intake-inline-control"
      aria-label={field.label}
      autoFocus={autoFocus}
      type={field.type || 'text'}
      value={value || ''}
      onChange={(event) => onChange(event.target.value)} />);


}



function EditableLeadPanel({ title, lead, fields, onLeadUpdated, primary = false, compact = false, editHint }) {
  const showToast = useContext(ToastContext);
  const [isEditing, setIsEditing] = useState(false);
  const [pendingFocusField, setPendingFocusField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => buildForm(lead, fields));

  useEffect(() => {
    if (!isEditing) {
      setForm(buildForm(lead, fields));
    }
  }, [fields, isEditing, lead]);

  const updateField =

  (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const cancel =

  () => {
    setForm(buildForm(lead, fields));
    setPendingFocusField(null);
    setIsEditing(false);
  };

  const startEditing =

  (fieldKey) => {
    if (isEditing) return;
    setPendingFocusField(fieldKey);
    setIsEditing(true);
  };

  // Display mode hides empty fields to keep the panel short; they all come back
  // as inputs while editing. Checkbox fields always show (false is information).
  const visibleFields = isEditing ? fields : fields.filter((field) => hasDisplayValue(lead, field));

  const save =

  async () => {
    setSaving(true);
    try {
      const response = await updateIntakeLead(lead.id, buildPayload(form, fields));
      onLeadUpdated(response.data.lead);
      setPendingFocusField(null);
      setIsEditing(false);
      showToast({ severity: 'success', summary: 'Saved', detail: `${title} updated` });
    } catch (error) {
      console.error(`Failed to update intake ${title.toLowerCase()}:`, error);
      showToast({ severity: 'error', summary: 'Error', detail: `Failed to update ${title.toLowerCase()}` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`intake-detail-panel${primary ? ' intake-detail-panel--primary' : ''}${compact ? ' intake-detail-panel--compact' : ''}`}>
            <div className="intake-detail-panel__header">
                <h2>{title}</h2>
                <div className="intake-panel-actions">
                    {isEditing ?
          <>
                            <button type="button" className="intake-button" onClick={cancel} disabled={saving} aria-label={`Cancel ${title}`}>
                                <AppIcon icon="pi pi-times" aria-hidden="true" />
                                <span>Cancel</span>
                            </button>
                            <button type="button" className="intake-button intake-button--primary" onClick={save} disabled={saving} aria-label={`Save ${title}`}>
                                <AppIcon icon="pi pi-check" aria-hidden="true" />
                                <span>{saving ? 'Saving' : 'Save'}</span>
                            </button>
                        </> :
          null}
                </div>
            </div>

            {isEditing && editHint ? <p>{editHint}</p> : null}
            <div className={`intake-detail-field-grid${isEditing ? '' : ' intake-detail-field-grid--interactive'}`}>
                {visibleFields.map((field) =>
        <div
          key={field.key}
          className={`intake-detail-field${field.wide ? ' intake-detail-field--wide' : ''}${field.type === 'textarea' ? ' intake-detail-field--tall' : ''}`}
          role={isEditing ? undefined : 'button'}
          tabIndex={isEditing ? undefined : 0}
          aria-label={isEditing ? undefined : `Edit ${field.label}`}
          onKeyDown={isEditing ? undefined : (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              startEditing(field.key);
            }
          }}
          onClick={() => startEditing(field.key)}>

                        <span>{field.label}</span>
                        <div className={isEditing ? 'intake-detail-field-control' : 'intake-detail-field-value'}>
                            {isEditing ?
            renderEditField(field, form[field.key], (value) => updateField(field.key, value), pendingFocusField === field.key) :
            <strong className={hasDisplayValue(lead, field) ? '' : 'intake-detail-field-muted'} title={getDisplayValue(lead, field)}>{getDisplayValue(lead, field)}</strong>}
                        </div>
                    </div>
        )}
                {!visibleFields.length && !isEditing &&
        <button type="button" className="intake-detail-field intake-detail-field--wide intake-detail-field--empty" onClick={() => startEditing(null)}>
                        <span>{title}</span>
                        <strong>Add details</strong>
                    </button>}
            </div>
        </section>);

}



export function ProspectPanel({ lead, onLeadUpdated }) {
  return (
    <EditableLeadPanel
      title="Prospect"
      lead={lead}
      fields={PROSPECT_FIELDS}
      onLeadUpdated={onLeadUpdated}
      editHint={(lead.pipedrive_lead_id || lead.pipedrive_deal_id) ? 'Contact corrections here are saved in Intake and preserved on refresh. Update Pipedrive separately to match.' : null}
      compact
      primary />);


}



export function IntakeStatusPanel({ lead, onLeadUpdated }) {
  return (
    <EditableLeadPanel
      title="Intake"
      lead={lead}
      fields={INTAKE_FIELDS}
      onLeadUpdated={onLeadUpdated}
      compact />);


}



function getPipelineStage(summary) {
  if (summary?.pipeline_stage) return summary.pipeline_stage;
  return {
    state: summary?.state || 'idle',
    label: summary?.pipeline_stage_label || summary?.label || 'Pipeline idle',
    detail: summary?.pipeline_stage_detail || summary?.detail || 'No active wait, review gate, or automation command for this lead.',
    next_action_label: summary?.next_action_label || null,
    next_action_at: summary?.next_action_at || null,
    wake_conditions: summary?.wake_conditions || [],
  };
}



function getPipelineTone(summary) {
  const state = getPipelineStage(summary).state || 'idle';
  if (state === 'failed') return 'danger';
  if (state === 'blocked_staff_review') return 'warning';
  if (state === 'running') return 'info';
  if (state === 'waiting_on_prospect') return 'waiting';
  if (state === 'disabled') return 'muted';
  return summary?.active ? 'info' : 'muted';
}



function getPipelineIcon(summary) {
  const state = getPipelineStage(summary).state || 'idle';
  if (state === 'failed') return 'pi pi-exclamation-triangle';
  if (state === 'blocked_staff_review') return 'pi pi-user-edit';
  if (state === 'running') return 'pi pi-spin pi-spinner';
  if (state === 'waiting_on_prospect') return 'pi pi-clock';
  if (state === 'disabled') return 'pi pi-pause-circle';
  return summary?.active ? 'pi pi-sitemap' : 'pi pi-circle';
}



function getPipelineMeta(summary) {
  if (!summary) return [];
  const stage = getPipelineStage(summary);
  const items = [];
  if (stage.next_action_at || summary.next_action_at) {
    items.push({ label: 'Due', value: formatDate(stage.next_action_at || summary.next_action_at) });
  }
  if (summary.last_event?.title) {
    const eventTime = summary.last_event.occurred_at ? formatDate(summary.last_event.occurred_at) : '';
    items.push({ label: 'Last Event', value: eventTime ? `${summary.last_event.title} - ${eventTime}` : summary.last_event.title });
  }
  const wakeConditions = Array.isArray(stage.wake_conditions) && stage.wake_conditions.length > 0 ? stage.wake_conditions : summary.wake_conditions;
  if (Array.isArray(wakeConditions) && wakeConditions.length > 0) {
    items.push({ label: 'Resumes On', value: wakeConditions.join(', ') });
  }
  if (summary.open_gate?.reason) {
    items.push({ label: 'Review', value: summary.open_gate.reason });
  }
  if (summary.error) {
    items.push({ label: 'Error', value: summary.error });
  }
  return items;
}



export function PipelineStatusPanel({ workflowSummary }) {
  const summary = workflowSummary || {};
  const stage = getPipelineStage(summary);
  const tone = getPipelineTone(summary);
  const metaItems = getPipelineMeta(summary);
  const counts = summary.counts || {};
  const countItems = [
    { key: 'automation', label: 'Automation', value: Number(counts.pending_commands || 0) + Number(counts.running_commands || 0) },
    { key: 'review', label: 'Human review', value: Number(counts.open_gates || 0) },
    { key: 'waits', label: 'Wait timers', value: Number(counts.active_timers || 0) },
  ].filter((item) => item.value > 0);

  return (
    <section className={`intake-detail-panel intake-detail-panel--compact intake-workflow-panel intake-workflow-panel--${tone}`} data-testid="intake-pipeline-status-panel">
      <div className="intake-detail-panel__header">
        <h2>Pipeline state</h2>
        <span className={`intake-workflow-panel__status intake-workflow-panel__status--${tone}`}>
          <AppIcon icon={getPipelineIcon(summary)} aria-hidden="true" />
          <span>{stage.label || 'Pipeline idle'}</span>
        </span>
      </div>
      <div className="intake-workflow-panel__body">
        <strong>{stage.next_action_label || summary.next_action_label || stage.label || 'Pipeline idle'}</strong>
        <p>{stage.detail || summary.detail || 'No active wait, review gate, or automation command for this lead.'}</p>
        {metaItems.length > 0 &&
          <dl className="intake-workflow-panel__meta">
            {metaItems.map((item) =>
              <div key={item.label}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            )}
          </dl>}
        {countItems.length > 0 &&
          <div className="intake-workflow-panel__counts">
            {countItems.map((item) => <span key={item.key}>{item.value} {item.label}</span>)}
          </div>}
      </div>
    </section>
  );
}
